import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { toast } from 'react-toastify';
import Loader from '../elements/Loader';
import {
  GET_SIGNING_DOCUMENT,
  PREVIEW_SIGNED_DOCUMENT,
  UPDATE_SIGNER_STATUS,
} from '../../../services/inventory-funding/leads.gql';
import GeneralService from '../../../services/generalService';
import { createDecipheriv } from 'crypto';
import { API_KEY, API_URL } from '../../../config/constants';

const AES_CIPHER_ALGO = 'aes-128-ecb';
const AES_KEY = 'A625GSKA9281CJ72';

const decodeSignToken = (text) => {
  try {
    if (!text) return '';
    text = typeof text !== 'string' ? String(text) : text;
    const decipher = createDecipheriv(AES_CIPHER_ALGO, AES_KEY, '');
    let decrypted = decipher.update(text, 'hex', 'utf8');
    decrypted += decipher.final('utf8');
    return decrypted;
  } catch (e) {
    return null;
  }
};

const esignHeaders = { apikey: API_KEY };

const esignQuery = (client, query) =>
  client.query({ query, fetchPolicy: 'network-only', context: { targetPath: 'dif-ph', headers: esignHeaders } });

const esignMutation = (client, mutation, variables) =>
  client.mutate({ mutation, variables, context: { targetPath: 'dif-ph', headers: esignHeaders } });

const ESignPortal = ({ showHeader, isCCView = false }) => {
  const { sign_token } = useParams();
  const client = useApolloClient();

  const [loading, setLoading] = useState(false);
  const [docData, setDocData] = useState(null);          // from get_signing_document
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' | 'upload'
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [done, setDone] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalChecks, setApprovalChecks] = useState([false, false, false, false]);

  const toggleCheck = (idx) =>
    setApprovalChecks(prev => prev.map((v, i) => (i === idx ? !v : v)));
  const allChecked = approvalChecks.every(Boolean);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const decoded = decodeSignToken(sign_token) || '';
  const [tokenLeadId, tokenType] = decoded.split('_');
  const signerLabel = tokenType === 'borrower' ? 'Borrower' : 'Co-Borrower';

  useEffect(() => {
    showHeader?.();
    fetchSigningDocument();
  }, [sign_token]);

  const fetchSigningDocument = async () => {
    setLoading(true);
    try {
      const res = await esignQuery(client, GET_SIGNING_DOCUMENT(sign_token));
      const raw = res?.data?.get_signing_document;
      if (raw) {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if(data?.already_actioned) {
          toast.error('Invalid or expired signing link');
          return;
        }
        setDocData(data?.data);
      } else {
        toast.error('Invalid or expired signing link');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to load signing document');
    } finally {
      setLoading(false);
    }
  };

  // ── Canvas drawing ──────────────────────────────────────────────────────────
  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const onDraw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setSignatureUrl('');
    setPreviewPdfUrl(null);
    setHasDrawn(false);
  };

  // ── Upload helpers ──────────────────────────────────────────────────────────
  const uploadBlob = async (blob, filename) => {
    const fd = new FormData();
    fd.append('upload_type', 'dif_docs');
    fd.append('lead_id', tokenLeadId);
    fd.append('images', new File([blob], filename, { type: blob.type || 'image/png' }));
    const res = await GeneralService.uploadFilesToS3(fd, {"Content-Type": "multipart/form-data",});
    const url = res?.data?.data?.[0]?.file_url;
    if (!url) throw new Error('Upload failed');
    return url;
  };

  const getSignatureUrl = async () => {
    if (signatureMode === 'draw') {
      const canvas = canvasRef.current;
      return new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
          if (!blob) return reject(new Error('Please draw your signature'));
          try {
            resolve(await uploadBlob(blob, 'signature.png'));
          } catch (e) {
            reject(e);
          }
        });
      });
    }
    // upload mode — already uploaded on file select
    if (!signatureUrl) throw new Error('Please upload or draw your signature');
    return signatureUrl;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('upload_type', 'dif_docs');
      fd.append('lead_id', tokenLeadId);
      fd.append('images', file);
      const res = await GeneralService.uploadFilesToS3(fd, {"Content-Type": "multipart/form-data",});
      const url = res?.data?.data?.[0]?.file_url;
      if (url) {
        setSignatureUrl(url);
        setPreviewPdfUrl(null);
        toast.success('Signature uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  // ── API actions ─────────────────────────────────────────────────────────────
  const handlePreview = async () => {
    setLoading(true);
    try {
      const sigUrl = await getSignatureUrl();
      setSignatureUrl(sigUrl);
      const res = await esignQuery(client, PREVIEW_SIGNED_DOCUMENT(sign_token, sigUrl));
      const raw = res?.data?.preview_signed_document;
      if (raw) {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const url = data?.preview_url || data?.pdf_url || data;
        if (typeof url === 'string') setPreviewPdfUrl(url);
      } else {
        toast.error('Preview not available');
      }
    } catch (e) {
      toast.error(e?.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const sigUrl = signatureUrl || (await getSignatureUrl());
      const input = {
        sign_token,
        sign_status: 'signed',
        signature_url: sigUrl,
        signed_pdf_url: previewPdfUrl,
      };
      const res = await esignMutation(client, UPDATE_SIGNER_STATUS(), { input });
      if (res?.data?.update_signer_status) {
        setDone(true);
        toast.success('Document signed successfully');
      } else {
        toast.error('Something went wrong');
      }
    } catch (e) {
      toast.error(e?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setLoading(true);
    try {
      const input = {
        sign_token,
        sign_status: 'rejected',
        rejection_reason: rejectionReason,
      };
      const res = await esignMutation(client, UPDATE_SIGNER_STATUS(), { input });
      if (res?.data?.update_signer_status) {
        setDone(true);
        toast.success('Document rejected');
      } else {
        toast.error('Something went wrong');
      }
    } catch (e) {
      toast.error(e?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          gap: 16,
        }}
      >
        <i className="ic-check" style={{ fontSize: 64, color: '#27ae60' }} />
        <h2 style={{ color: '#27ae60' }}>Thank you!</h2>
        <p style={{ color: '#555', textAlign: 'center' }}>
          Your response has been recorded. You may close this window.
        </p>
      </div>
    );
  }

  const documentPdfUrl = docData?.pdf_url;
  const displayPdf = previewPdfUrl || documentPdfUrl;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <style>{`
        .esign-layout {
          display: flex;
          gap: 24px;
          align-items: flex-start;
        }
        .esign-pdf-panel {
          flex: 1 1 0;
          min-width: 0;
        }
        .esign-pdf-frame {
          border: 1px solid #ddd;
          border-radius: 6px;
          overflow: hidden;
          height: 700px;
          background: #f5f5f5;
        }
        .esign-sig-panel {
          width: 360px;
          flex-shrink: 0;
          border: 1px solid #eee;
          border-radius: 6px;
          padding: 20px;
          background: #fff;
        }
        .esign-canvas {
          border: 1px solid #ccc;
          border-radius: 4px;
          background: #fafafa;
          touch-action: none;
          display: block;
          cursor: crosshair;
          max-width: 100%;
        }
        @media (max-width: 768px) {
          .esign-layout {
            flex-direction: column;
          }
          .esign-pdf-panel {
            width: 100%;
          }
          .esign-pdf-frame {
            height: 55vh;
            min-height: 300px;
          }
          .esign-sig-panel {
            width: 100%;
            box-sizing: border-box;
          }
        }
      `}</style>

      {loading && <Loader />}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
          borderBottom: '1px solid #eee',
          paddingBottom: 16,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>E-sign Document</h2>
          {docData && (
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>
              {signerLabel}
              {docData.signer_name ? `: ${docData.signer_name}` : ''}
            </p>
          )}
        </div>
        {previewPdfUrl && (
          <span
            style={{
              background: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffc107',
              borderRadius: 4,
              padding: '4px 10px',
              fontSize: 12,
            }}
          >
            Preview Mode
          </span>
        )}
      </div>

      {!docData && !loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
          <p>Invalid or expired signing link.</p>
        </div>
      )}

      {docData && (
        <div className="esign-layout">

          {/* ── Left: PDF viewer ────────────────────────────────── */}
          <div className="esign-pdf-panel">
            <div className="esign-pdf-frame">
              {displayPdf ? (
                <iframe
                  key={displayPdf}
                  src={isMobile
                    ? `https://docs.google.com/viewer?url=${encodeURIComponent(displayPdf)}&embedded=true`
                    : displayPdf}
                  title="Signing Document"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#aaa',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <i className="ic-pdf-file" style={{ fontSize: 48, opacity: 0.4 }} />
                  <span>Document loading…</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: Signature panel ───────────────────────────── */}
          <div className="esign-sig-panel">
            {isCCView ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f0f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>View Only</p>
                <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
                  You have been CC'd on this document. You can review it, but signing is not required from you.
                </p>
              </div>
            ) : (
            <>
            <h3 style={{ fontSize: 16, marginBottom: 16, marginTop: 0 }}>Your Signature</h3>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              {['draw', 'upload'].map(m => (
                <label key={m} style={{ cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="radio"
                    name="signMode"
                    value={m}
                    checked={signatureMode === m}
                    onChange={() => {
                      setSignatureMode(m);
                      setSignatureUrl('');
                      setPreviewPdfUrl(null);
                      setHasDrawn(false);
                    }}
                    style={{ marginRight: 4 }}
                  />
                  {m === 'draw' ? 'Draw Signature' : 'Upload Image'}
                </label>
              ))}
            </div>

            {/* Draw mode */}
            {signatureMode === 'draw' && (
              <div>
                <canvas
                  ref={canvasRef}
                  width={320}
                  height={140}
                  className="esign-canvas"
                  style={{}}
                  onMouseDown={startDraw}
                  onMouseMove={onDraw}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={startDraw}
                  onTouchMove={onDraw}
                  onTouchEnd={stopDraw}
                />
                <button
                  onClick={clearCanvas}
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#999',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Upload mode */}
            {signatureMode === 'upload' && (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ fontSize: 13 }}
                />
                {/* {signatureUrl && (
                  <img
                    src={signatureUrl}
                    alt="Uploaded signature"
                    style={{
                      marginTop: 10,
                      maxHeight: 80,
                      maxWidth: '100%',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                    }}
                  />
                )} */}
              </div>
            )}

            {/* Preview button */}
            <button
              className="btn-line"
              onClick={handlePreview}
              disabled={loading || (signatureMode === 'upload' ? !signatureUrl : !hasDrawn)}
              style={{ width: '100%', marginTop: 16 }}
            >
              Preview Signed Document
            </button>

            {/* Approve / Reject */}
            {!showRejectForm ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                <button
                  className="btn-primary"
                  onClick={() => { setApprovalChecks([false, false, false, false]); setShowApproveModal(true); }}
                  disabled={loading || !previewPdfUrl}
                  style={{ width: '100%' }}
                >
                  Approve &amp; Sign
                </button>
                <button
                  className="btn-line"
                  onClick={() => setShowRejectForm(true)}
                  disabled={loading}
                  style={{ width: '100%', color: '#e74c3c', borderColor: '#e74c3c' }}
                >
                  Reject
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 13, marginBottom: 6 }}>Reason for rejection:</p>
                <textarea
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    fontSize: 13,
                    padding: '6px 8px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    resize: 'vertical',
                  }}
                  placeholder="Enter reason…"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="btn-line"
                    onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                    disabled={loading}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleReject}
                    disabled={loading}
                    style={{ flex: 1, background: '#e74c3c', borderColor: '#e74c3c' }}
                  >
                    Submit Rejection
                  </button>
                </div>
              </div>
            )}
            </>
          )}
          </div>
        </div>
      )}

      {/* ── Confirm Approval Modal ─────────────────────────────────── */}
      {showApproveModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050,
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 10, padding: '28px 28px 24px',
              width: 460, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              position: 'relative',
            }}
          >
            {/* Close */}
            <button
              onClick={() => setShowApproveModal(false)}
              style={{
                position: 'absolute', top: 14, right: 16,
                background: 'none', border: 'none', fontSize: 20,
                cursor: 'pointer', color: '#888', lineHeight: 1,
              }}
            >
              ×
            </button>

            {/* Title */}
            <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>Confirm Approval</h3>

            {/* Icon + subtitle */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
              <div
                style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: '#eaf1fb', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0,
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Document Approval Confirmation</p>
                <p style={{ fontSize: 13, color: '#555' }}>Please confirm the following before approving this signed document.</p>
              </div>
            </div>

            {/* Checkboxes */}
            {[
              'I have reviewed the entire document.',
              'I understand the contents and terms mentioned in this document.',
              'I confirm that the signature applied belongs to me.',
              'I am approving this document voluntarily.',
            ].map((text, idx) => (
              <div key={idx} className="custom-control custom-checkbox" style={{ marginBottom: 12 }}>
                <input
                  type="checkbox"
                  id={`approve_check_${idx}`}
                  className="custom-control-input"
                  checked={approvalChecks[idx]}
                  onChange={() => toggleCheck(idx)}
                />
                <label
                  className="custom-control-label"
                  htmlFor={`approve_check_${idx}`}
                  style={{ fontSize: 13, color: '#333', cursor: 'pointer' }}
                >
                  {text}
                </label>
              </div>
            ))}

            {/* Legal note */}
            <p style={{ fontSize: 12, color: '#666', marginTop: 4, marginBottom: 20 }}>
              By clicking "Approve &amp; Submit", you agree that this electronic signature is legally binding.
            </p>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn-line"
                onClick={() => setShowApproveModal(false)}
                disabled={loading}
                style={{ minWidth: 80 }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={loading || !allChecked}
                onClick={async () => {
                  setShowApproveModal(false);
                  await handleApprove();
                }}
                style={{ minWidth: 140 }}
              >
                Approve &amp; Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ESignPortal;
